import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api, { fileUrl } from "../../lib/api";
import { useSiteConfig } from "../../context/SiteConfigContext";
import AnimatedImage from "../../components/AnimatedImage";
import HomepageBlock from "../../components/HomepageBlock";
import HeroImage from "../../components/HeroImage";
import SeoHead from "../../components/SeoHead";

export default function Home() {
    const { config } = useSiteConfig();
    const [programs, setPrograms] = useState([]);
    const [news, setNews] = useState([]);

    useEffect(() => {
        api.get("/programs").then(({ data }) => setPrograms(data.slice(0, 6))).catch(() => {});
        api.get("/news").then(({ data }) => setNews(data.slice(0, 3))).catch(() => {});
    }, []);

    if (!config) return null;

    return (
        <div data-testid="home-page">
            <SeoHead pageKey="home" />
            {/* HERO */}
            <section className="relative min-h-[78vh] flex items-end overflow-hidden" data-testid="home-hero">
                <HeroImage
                    src={config.home_hero_image}
                    alt="YACRRD communities"
                    animation={config.hero_animation || "ken-burns"}
                    overlay={config.hero_overlay_style || "dark"}
                    overlayOpacity={config.hero_overlay_opacity ?? 55}
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 w-full">
                    <div className="max-w-3xl text-white">
                        <span className="inline-block text-xs uppercase tracking-[0.22em] mb-6 px-3 py-1 border border-white/30 rounded-sm" data-testid="hero-tagline">
                            {config.tagline}
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6" data-testid="hero-title">
                            {config.home_hero_title}
                        </h1>
                        <p className="text-base sm:text-lg text-white/90 max-w-2xl mb-10" data-testid="hero-subtitle">
                            {config.home_hero_subtitle}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link
                                to={config.home_cta_primary_link || "/contact"}
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
                                data-testid="hero-cta-primary"
                            >
                                {config.home_cta_primary_label} <ArrowRight size={16} />
                            </Link>
                            <Link
                                to={config.home_cta_secondary_link || "/contact"}
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm border border-white/60 text-white hover:bg-white/10 transition-colors text-sm font-medium"
                                data-testid="hero-cta-secondary"
                            >
                                {config.home_cta_secondary_label} <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* MISSION STATEMENT */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="home-mission">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-2 hidden lg:block">
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Our Mission</div>
                    </div>
                    <div className="lg:col-span-10">
                        <p className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-foreground mb-8 max-w-5xl">
                            {config.home_mission_text}
                        </p>
                        <p className="text-base text-muted-foreground max-w-3xl">
                            {config.home_supporting_text}
                        </p>
                    </div>
                </div>
            </section>

            {/* FEATURED PROGRAMS */}
            <section className="bg-muted/40 py-24" data-testid="home-programs">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">{config.home_programs_kicker}</div>
                            <h2 className="font-display text-4xl sm:text-5xl text-foreground max-w-2xl">
                                {config.home_programs_title}
                            </h2>
                        </div>
                        <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="programs-view-all">
                            {config.home_programs_view_all_label} <ArrowRight size={14} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {programs.map((p, i) => (
                            <Link
                                to={`/programs/${p.slug}`}
                                key={p.id}
                                className={`group relative overflow-hidden rounded-sm border border-border bg-card hover:border-primary/60 transition-colors ${
                                    i === 0 ? "sm:col-span-2 lg:col-span-2 lg:row-span-2" : ""
                                }`}
                                data-testid={`program-card-${p.slug}`}
                            >
                                <div className={`${i === 0 ? "aspect-[16/10]" : "aspect-[4/3]"} overflow-hidden`}>
                                    <AnimatedImage src={p.image_url} alt={p.title} className="w-full h-full" />
                                </div>
                                <div className="p-6">
                                    <div className="text-xs uppercase tracking-[0.18em] text-primary mb-2">Program {String(i + 1).padStart(2, "0")}</div>
                                    <h3 className="font-display text-2xl mb-3 leading-tight group-hover:text-primary transition-colors">
                                        {p.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground line-clamp-3">{p.summary}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* NEWS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="home-news">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">Stories From The Field</div>
                        <h2 className="font-display text-4xl sm:text-5xl text-foreground max-w-2xl">Resilience in action</h2>
                    </div>
                    <Link to="/news" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                        All stories <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    {news.map((n) => (
                        <Link
                            to={`/news/${n.slug}`}
                            key={n.id}
                            className="group block rounded-sm overflow-hidden border border-border bg-card hover:border-primary/60 transition-colors"
                            data-testid={`news-card-${n.slug}`}
                        >
                            <div className="aspect-[4/3]">
                                <AnimatedImage src={n.image_url} alt={n.title} className="w-full h-full" />
                            </div>
                            <div className="p-6">
                                <div className="text-xs uppercase tracking-[0.18em] text-secondary mb-2">{n.category}</div>
                                <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-primary transition-colors">{n.title}</h3>
                                <p className="text-sm text-muted-foreground line-clamp-3">{n.excerpt}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CUSTOM HOMEPAGE BLOCKS (admin-managed) */}
            {(config.homepage_blocks || []).length > 0 && (
                <div data-testid="home-custom-blocks">
                    {(config.homepage_blocks || []).map((b) => <HomepageBlock key={b.id} block={b} />)}
                </div>
            )}

            {/* CTA */}
            <section className="bg-primary text-primary-foreground" data-testid="home-cta">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <h2 className="font-display text-4xl sm:text-5xl mb-4">Partner with us. Walk with communities.</h2>
                        <p className="text-primary-foreground/80 max-w-2xl">
                            Join donors, peers, and government partners helping us scale resilience, livelihoods, and peace across South Sudan.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-background text-foreground hover:bg-background/90 text-sm font-medium"
                            data-testid="cta-partner-button"
                        >
                            Start a conversation <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
